import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ShieldCheck, ArrowRight, CheckCircle2, X, ChevronLeft, ChevronRight } from "lucide-react";

const FALLBACK_SERVICES = [
  {
    id: "3d72b1af-6138-444d-b888-eb69dc20e768",
    slug_name: "ovenlysvinduer",
    title: { da: "Ovenlysvinduer" },
    meta_description: { da: "Ovenlysvinduer er en oplagt måde til at få mere lys ind i hjemmet. Hos BMT TAG ApS har vi solid erfaring med udskiftning af tag og ovenlysvinduer. Læs mere." },
    image: ["https://assets.auxodev.dk/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/skylight-window-roof.jpg"]
  },
  {
    id: "50d79ecf-9bcb-4caf-8ebe-7cbeb842a105",
    slug_name: "listetaekning",
    title: { da: "Listetækning" },
    meta_description: { da: "Listetækning giver taget et smukt, arkitektonisk udtryk med markante linjer og skyggevirkninger. Læs mere om vores professionelle listetækning her." },
    image: ["https://assets.auxodev.dk/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/modern-danish-roof-roofing.jpg"]
  },
  {
    id: "5f5d7f98-6233-4eb3-ab56-2094a522af7c",
    slug_name: "tagdaekning-med-shingles",
    title: { da: "Tagdækning med shingles" },
    meta_description: { da: "BMT TAG ApS har solid erfaring med lægning af shingles, og vi tilbyder både rådgivning samt tagdækning med shingles i Herning og omegn. Læs mere." },
    image: ["https://assets.auxodev.dk/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/image.png"]
  },
  {
    id: "a511c181-b756-477e-9e75-80a13a565762",
    slug_name: "derbigum-tagmembran",
    title: { da: "Derbigum Tagmembran" },
    meta_description: { da: "Tagmaterialet Derbigum er en blød og smidig membran af APP-modificeret asfalt med to bærere: glasfibervæv og polyesterfilt. Læs om anvendelse og levetid her." },
    image: ["https://assets.auxodev.dk/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/commercial-building-roof.jpg"]
  }
];

export default function Home() {
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [galleryImages, setGalleryImages] = useState([]);
  const [activeImage, setActiveImage] = useState(null);
  const [formStatus, setFormStatus] = useState({ type: null, message: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  const [serviceOffset, setServiceOffset] = useState(0);
  const [cols, setCols] = useState(4);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setCols(1);
      } else if (width < 1024) {
        setCols(2);
      } else {
        setCols(4);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Formular-state
  const [formData, setFormData] = useState({
    navn: "",
    telefon: "",
    email: "",
    beskrivelse: "",
    website_url: "", // Honeypot
  });

  useEffect(() => {
    // Hent forside-meta fra CMS
    fetch("https://api.dev.auxo.dk/public/content/pages")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const indexPage = data.find((p) => p.slug_name === "index");
          if (indexPage) {
            if (indexPage.meta_title?.da) {
              document.title = indexPage.meta_title.da;
            } else {
              document.title = "BMT TAG ApS – Professionel tagdækning og tagpap i Herning";
            }
            if (indexPage.meta_description?.da) {
              const metaDesc = document.querySelector('meta[name="description"]');
              if (metaDesc) {
                metaDesc.setAttribute("content", indexPage.meta_description.da);
              }
            }
          }
        }
      })
      .catch((err) => {
        console.error("Fejl ved hentning af forside-meta:", err);
        document.title = "BMT TAG ApS – Professionel tagdækning og tagpap i Herning";
      });

    // Hent ydelser fra CMS
    fetch("https://api.dev.auxo.dk/public/content/services")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data);
        }
      })
      .catch((err) => console.error("Fejl ved hentning af ydelser:", err));

    // Hent reference-galleri fra CMS
    fetch("https://api.dev.auxo.dk/public/content/gallery")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const refGallery = data.find((g) => g.slug_name === "referencer");
          if (refGallery && refGallery.images) {
            setGalleryImages(refGallery.images);
          }
        }
      })
      .catch((err) => console.error("Fejl ved hentning af galleri:", err));
  }, []);

  const maxOffset = Math.max(0, services.length - cols);

  const handlePrev = () => {
    setServiceOffset((prev) => (prev === 0 ? maxOffset : prev - 1));
  };

  const handleNext = () => {
    setServiceOffset((prev) => (prev === maxOffset ? 0 : prev + 1));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: null, message: null });

    // Validering
    if (!formData.navn || !formData.email || !formData.telefon) {
      setFormStatus({
        type: "error",
        message: "Venligst udfyld alle påkrævede felter (Navn, Telefon og Email).",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setFormStatus({
          type: "success",
          message: "Mange tak for din henvendelse! Vi har modtaget din tilbudsforespørgsel og vender tilbage hurtigst muligt.",
        });
        setFormData({
          navn: "",
          telefon: "",
          email: "",
          beskrivelse: "",
          website_url: "",
        });
      } else {
        setFormStatus({
          type: "error",
          message: "Der skete en fejl ved afsendelsen. Prøv venligst igen eller kontakt os direkte på tlf. 23 28 71 83.",
        });
      }
    } catch (err) {
      console.error("Formularfejl:", err);
      setFormStatus({
        type: "error",
        message: "Netværksfejl. Tjek venligst din internetforbindelse og prøv igen.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-20 pb-20">
      {/* Hero Sektion */}
      <section className="relative bg-brand-blue-dark text-white overflow-hidden">
        {/* Baggrundsbillede med overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/modern-danish-roof-roofing.jpg"
            alt="Moderne tagdækning i Danmark"
            className="h-full w-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue-dark via-brand-blue-dark/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <div className="max-w-2xl space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white">
              <ShieldCheck className="h-5 w-5" />
              Op til 15 års forsikringsdækket garanti
            </div>
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl font-display leading-tight sm:leading-tight">
              Professionel tagdækning og tagpap i Herning
            </h1>
            <p className="text-lg sm:text-xl text-slate-300">
              Hos BMT TAG ApS har vi over 35 års erfaring med etablering af nyt tag og renovering af tagpap. Vi leverer langtidsholdbare kvalitetsløsninger til både private og erhverv.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                to="/indhent-tilbud"
                className="inline-flex items-center justify-center rounded-md bg-brand-accent px-6 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-brand-accent-hover cursor-pointer"
              >
                Indhent tilbud
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="tel:+4523287183"
                className="inline-flex items-center justify-center rounded-md border border-white bg-white/10 px-6 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-white/20 cursor-pointer"
              >
                Ring 23 28 71 83
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Kort introduktion - Split layout asymmetrical design reference */}
      <section className="bg-slate-50 py-20 border-y border-brand-neutral-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-20 items-center">
            {/* Left Side: Overlapping image gallery with two vertically oriented rectangular images */}
            <div className="lg:col-span-6 relative h-[400px] sm:h-[480px] lg:h-[450px] xl:h-[580px] w-full max-w-md mx-auto lg:max-w-none pr-6 pb-6">
              {/* First Image (Top-Left, vertically oriented) */}
              <div className="absolute top-0 left-0 w-[58%] h-[82%] rounded-none overflow-hidden shadow-xl border border-brand-neutral-border z-10">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=800,fit=cover,quality=80,format=auto/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/shingles-roof-house.jpg"
                  alt="Tagdækker i gang med tagpap"
                  className="h-full w-full object-cover rounded-none"
                />
              </div>
              {/* Second Image (Staggered slightly downward and to the right, vertically oriented) */}
              <div className="absolute bottom-0 right-0 w-[58%] h-[82%] rounded-none overflow-hidden shadow-2xl border border-brand-neutral-border z-20 translate-x-4 translate-y-4">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=800,fit=cover,quality=80,format=auto/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/modern-danish-roof-roofing.jpg"
                  alt="Moderne tagdækning i Herning"
                  className="h-full w-full object-cover rounded-none"
                />
              </div>
            </div>

            {/* Right Side: Typography-focused content block */}
            <div className="lg:col-span-6 space-y-6">
              {/* Eyebrow with horizontal line decoration to its left */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-[2px] bg-brand-accent" />
                <span className="text-xs font-bold tracking-wider text-brand-accent uppercase">
                  Hvem er vi?
                </span>
              </div>

              {/* Medium-weight display heading */}
              <h2 className="text-3xl font-medium tracking-tight text-brand-blue-dark sm:text-4xl font-display leading-tight">
                Ekspert inden for tagdækning i Herning og omegn
              </h2>

              {/* Slightly larger and bolder lead paragraph acting as a subheading */}
              <p className="text-lg font-semibold text-slate-700 leading-relaxed">
                Leder du efter en ekspert i tagdækning? Så er du kommet til det rette sted. Hos BMT TAG ApS har vi mange års erfaring inden for tagdækning med tagpap.
              </p>

              {/* Lighter body paragraph */}
              <div className="text-slate-500 text-base leading-relaxed space-y-4">
                <p>
                  Vi løser hver dag en række forskellige opgaver med etablering af nyt tag og diverse renoveringsopgaver. Vi henvender os både til private og erhverv.
                </p>
                <p>
                  Hos os kan du altid være sikker på, at du får netop den løsning til din tagdækningsopgave, som opfylder kravene til kvalitet, holdbarhed og god økonomi.
                </p>
              </div>

              {/* Outlined CTA button */}
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center rounded-md border-2 border-brand-accent px-6 py-3 text-base font-semibold text-brand-accent hover:bg-brand-accent hover:text-white transition-colors cursor-pointer"
                >
                  Kontakt os i dag
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Udvalgte arbejdsopgaver */}
      <section className="relative w-full overflow-hidden h-[600px] md:h-[750px] bg-brand-blue-dark">
        <div
          className="flex h-full transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(-${serviceOffset * (100 / cols)}%)`,
          }}
        >
          {services.map((service) => (
            <Link
              key={service.id}
              to={`/arbejdsopgaver/${service.slug_name}`}
              className="w-full md:w-1/2 lg:w-1/4 shrink-0 h-full relative group overflow-hidden border-none rounded-none transition-all duration-500 ease-in-out hover:w-[110%] md:hover:w-[55%] lg:hover:w-[27.5%] block z-10 hover:z-20"
            >
              {/* Background Image */}
              <img
                src={
                  service.image && service.image[0]
                    ? `https://assets.auxodev.dk/cdn-cgi/image/width=800,height=1200,fit=cover,quality=80,format=auto/${service.image[0].replace("https://assets.auxodev.dk/", "")}`
                    : "https://assets.auxodev.dk/cdn-cgi/image/width=800,height=1200,fit=cover,quality=80,format=auto/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/modern-danish-roof-roofing.jpg"
                }
                alt={service.title.da}
                className="absolute inset-0 w-full h-full object-cover rounded-none"
              />

              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/10 z-10 transition-all duration-300 group-hover:from-black/95 group-hover:via-black/60" />

              {/* Title: Aligned to left and a little bit up, horizontal, text wraps nicely so it doesn't go out of the box */}
              <div className="absolute left-6 right-6 bottom-16 z-20 pointer-events-none">
                <span className="text-xl md:text-2xl font-bold tracking-wide uppercase text-white font-display block drop-shadow-md">
                  {service.title.da}
                </span>
                <span className="text-xs font-semibold text-white uppercase tracking-widest mt-2 block transition-transform duration-300 group-hover:translate-x-1">
                  Se ydelse →
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Floating action buttons anchored to the middle right edge */}
        {services.length > cols && (
          <div className="absolute right-6 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-3">
            <button
              onClick={handlePrev}
              className="w-12 h-12 rounded-full bg-brand-accent text-white hover:bg-brand-accent-hover flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer"
              aria-label="Forrige"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-brand-accent text-white hover:bg-brand-accent-hover flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer"
              aria-label="Næste"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
        )}
      </section>



      {/* Kontaktblok & Formular til tilbud */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 bg-white rounded-2xl border border-brand-neutral-border shadow-md overflow-hidden">
          {/* Kontaktinfo (venstre) */}
          <div className="lg:col-span-5 bg-brand-blue text-white p-8 sm:p-12 flex flex-col justify-between space-y-8">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold font-display text-white">
                Kontakt BMT TAG ApS
              </h3>
              <p className="text-slate-300">
                Har du spørgsmål, eller ønsker du et uforpligtende tilbud? Kontakt os i dag – vi svarer hurtigt.
              </p>
              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-white shrink-0 mt-0.5" />
                  <p className="text-slate-200">
                    Åkirkebyvej 4<br />
                    7400 Herning
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-white shrink-0" />
                  <a href="tel:+4523287183" className="text-slate-200 hover:text-white/70 transition-colors whitespace-nowrap">
                    23 28 71 83
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-white shrink-0" />
                  <a href="mailto:mb@bmttag.dk" className="text-slate-200 hover:text-white/70 transition-colors whitespace-nowrap">
                    mb@bmttag.dk
                  </a>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-white/10 text-xs text-slate-400 space-y-1">
              <p>BMT TAG ApS</p>
              <p>CVR-nr.: 45546845</p>
              <p>PBS nr.: 85333186</p>
            </div>
          </div>

          {/* Formular (højre) */}
          <div className="lg:col-span-7 p-8 sm:p-12 space-y-6">
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-brand-blue-dark">
                Indhent et uforpligtende tilbud
              </h3>
              <p className="text-sm text-slate-500">
                Udfyld formularen, så vender vi tilbage med et skarpt tilbud på din tagopgave.
              </p>
            </div>

            {formStatus.message && (
              <div
                className={`p-4 rounded-md text-sm ${
                  formStatus.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {formStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot - ABSOLUT SKJULT */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke)</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label htmlFor="navn" className="text-xs font-bold uppercase text-brand-blue-dark">
                    Navn <span className="text-brand-accent">*</span>
                  </label>
                  <input
                    type="text"
                    id="navn"
                    name="navn"
                    required
                    value={formData.navn}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-brand-neutral-border bg-slate-50 px-4 py-2.5 text-sm focus:border-brand-accent focus:bg-white focus:outline-none"
                    placeholder="Dit fulde navn"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="telefon" className="text-xs font-bold uppercase text-brand-blue-dark">
                    Telefonnummer <span className="text-brand-accent">*</span>
                  </label>
                  <input
                    type="tel"
                    id="telefon"
                    name="telefon"
                    required
                    value={formData.telefon}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-brand-neutral-border bg-slate-50 px-4 py-2.5 text-sm focus:border-brand-accent focus:bg-white focus:outline-none"
                    placeholder="Tlf. nr."
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label htmlFor="email" className="text-xs font-bold uppercase text-brand-blue-dark">
                  E-mail <span className="text-brand-accent">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-brand-neutral-border bg-slate-50 px-4 py-2.5 text-sm focus:border-brand-accent focus:bg-white focus:outline-none"
                  placeholder="din@email.dk"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="beskrivelse" className="text-xs font-bold uppercase text-brand-blue-dark">
                  Beskrivelse af opgaven
                </label>
                <textarea
                  id="beskrivelse"
                  name="beskrivelse"
                  rows="4"
                  value={formData.beskrivelse}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-brand-neutral-border bg-slate-50 px-4 py-2.5 text-sm focus:border-brand-accent focus:bg-white focus:outline-none"
                  placeholder="Beskriv din opgave, f.eks. tagtype, areal, hældning osv."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-md bg-brand-accent py-3 text-sm font-bold text-white shadow-sm hover:bg-brand-accent-hover transition-colors disabled:bg-slate-400 cursor-pointer"
              >
                {isSubmitting ? "Sender..." : "Send tilbudsforespørgsel"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Lightbox / Stor visning af galleribilleder */}
      {activeImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <button
            onClick={() => setActiveImage(null)}
            className="absolute top-4 right-4 text-white hover:text-brand-accent p-2 cursor-pointer"
          >
            <X className="h-8 w-8" />
          </button>
          <div className="max-w-4xl max-h-[85vh] overflow-hidden rounded-none">
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${activeImage.replace("https://assets.auxodev.dk/", "")}`}
              alt="Reference stor visning"
              className="w-full h-auto max-h-[85vh] object-contain rounded-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}
